import React, { FC } from 'react';
import { LuvImage } from '@/components';
import classNames from 'classnames';
import { InternalLink } from '@/components/ui/InternalLink';
import { TypeProductCardFields } from '@/core/generated-types';
import { ShopifyProduct } from '@/lib/shopify';
import { Entry } from 'contentful';
import { ExtendedProductFields } from '@/core/contentfulConfig';
import { PRODUCT_EVENT_TYPES, trackProductEvent } from '@/utils/rudderstack';
import { usePathname } from '@/navigation';

export interface ExtendedProductCardProps
  extends Omit<TypeProductCardFields, 'product'> {
  product: Entry<ExtendedProductFields>;
}

const imageSizes = {
  playKit: {
    width: 800,
    height: 450,
  },
  standalone: {
    width: 430,
    height: 430,
  },
} as const;

export const ProductCard: FC<ExtendedProductCardProps> = ({
  entryName,
  product,
  productLink,
  featuredImageOverride,
}) => {
  const pathname = usePathname();
  const shopifyProduct =
    product.fields?.shopifyProduct || ({} as ShopifyProduct);
  const [defaultVariant] = shopifyProduct.variants || [];

  const { listingImage, title, ageRange, isPlayKit } = product.fields || {};
  const featuredImage = featuredImageOverride || listingImage;
  const imageSize = isPlayKit ? imageSizes.playKit : imageSizes.standalone;
  const productCardClasses = classNames(
    `mx-auto max-w-[${imageSize.width}px]`,
    'text-left',
  );

  const onProductClick = () => {
    const payload = {
      url: pathname || '',
      productId: shopifyProduct?.id || '',
      sku: shopifyProduct?.variants[0].sku || '',
      category: shopifyProduct?.productType || '',
      name: title,
      ageRange: ageRange || '',
      ageUnits: '',
      price: shopifyProduct?.variants[0].price?.amount || 0,
      quantity: 1,
    };

    if (shopifyProduct) {
      trackProductEvent(PRODUCT_EVENT_TYPES.PRODUCT_CLICKED, payload);
    }
  };

  return (
    <InternalLink
      link={productLink}
      classes="inline-block product-card"
      hideArrow
      onClick={onProductClick}
    >
      <div
        className={classNames(productCardClasses, 'product-card mb-4 md:mb-0')}
        data-testid={`product-card-${entryName}`}
        data-ga-section="product-card"
        data-ga-name={shopifyProduct?.title}
        data-ga-id={defaultVariant?.sku}
        data-ga-price={defaultVariant?.price?.amount}
        data-ga-category={shopifyProduct?.productType}
        data-ga-product-id={shopifyProduct?.id}
        data-ga-variant-id={defaultVariant?.id}
        data-ga-currency-code={defaultVariant?.price?.currencyCode}
        data-ga-variant-title={defaultVariant?.title}
      >
        <div className="product-card-image">
          <LuvImage
            height={imageSize.height}
            width={imageSize.width}
            imageData={featuredImage}
            alt={featuredImage?.fields?.altText}
          />
        </div>
        <div className="product-card-text">
          <p className="product-card-title mt-2 text-h3 font-semibold md:text-h2 leading-5">
            {title}
          </p>
          {ageRange && (
            <p className="product-card-ageRange text-base md:text-h3">
              {ageRange}
            </p>
          )}
        </div>
      </div>
    </InternalLink>
  );
};
